/**
 * Console-web's own LOCAL mirror of `platform/clinical-study`'s closed vocabularies and format
 * constants (recon GAP 3, SL-0041 Resolution R5). Verified: the root `eslint.config.mjs`'s
 * `boundaries/dependencies` rule applies only to `files: ['platform/*\/src/**\/*.ts',
 * 'packages/*\/src/**\/*.ts']` — it does not evaluate `products/*` files, so it does not
 * literally fire on an attempted cross-workspace import here. The values below are mirrored, not
 * imported, because: (1) `docs/project-context.md` Module Placement rules 2 and 4 make a
 * module's own domain code reachable only over its `/v1` HTTP contract, never by compile-time
 * import — importing `platform/clinical-study` source into `console-web` is itself the
 * violation those rules exist to prevent, an Escalation Boundary; (2) `platform/clinical-study`
 * is not a declared pnpm workspace dependency of `@console/console-web`
 * (`products/console-web/package.json`), so no module specifier resolves it; and (3)
 * `tsconfig.app.json`'s `rootDir: "./src"` under `composite: true` makes a relative import
 * reaching outside `src/` a TypeScript project-reference violation regardless. Each value is
 * verified byte-for-byte against its `platform/clinical-study` source at SL-0041 authoring time
 * — keep both sides in sync by hand if either changes.
 */

/** Mirrors `platform/clinical-study/src/study-subject/domain/definitive-identifier.ts`'s
 * `DEFINITIVE_IDENTIFIER_PATTERN` (FR-9.4, FR-9.28, FR-9.30) — digits only, 3 to 8 digits. */
export const DEFINITIVE_IDENTIFIER_PATTERN = /^\d{3,8}$/;

/** Mirrors `platform/clinical-study/src/study-subject/domain/definitive-identifier-correction-reason.ts`'s
 * `DEFINITIVE_IDENTIFIER_CORRECTION_REASONS` (FR-9.30) — the closed correction-reason vocabulary,
 * never free text. Drives the RHF `Controller` + shadcn `Select` + `z.enum` triad in
 * `IdentifierCorrectionSection.tsx`; never a free-text control. */
export const DEFINITIVE_IDENTIFIER_CORRECTION_REASONS = [
  'transcription-error',
  'ecrf-identifier-updated',
  'incorrect-binding',
  'other',
] as const;
export type DefinitiveIdentifierCorrectionReason =
  (typeof DEFINITIVE_IDENTIFIER_CORRECTION_REASONS)[number];

/** Mirrors `platform/clinical-study/src/study-timepoint/domain/study-timepoint.ts`'s
 * `STUDY_TIMEPOINT_TYPES` (FR-9.6, FR-9.7) — exactly one `TREATMENT_ANCHOR` (offset 0) plus zero
 * or more `FOLLOW_UP` entries per active set. */
export const STUDY_TIMEPOINT_TYPES = ['TREATMENT_ANCHOR', 'FOLLOW_UP'] as const;
export type StudyTimepointType = (typeof STUDY_TIMEPOINT_TYPES)[number];

/** Mirrors the `role` enum in
 * `platform/clinical-study/src/study-membership/interface/study-membership-request.schema.ts`
 * (FR-9.3, FR-9.5) — the study-confined roles a study administrator may assign: the two
 * operational ones and the data manager (decision 105). */
export const STUDY_MEMBERSHIP_ROLES = [
  'investigator',
  'assistant_coordinator',
  'data_manager',
] as const;
export type StudyMembershipRole = (typeof STUDY_MEMBERSHIP_ROLES)[number];

/** Mirrors `platform/clinical-study/src/study-subject/domain/contact-log-entry.ts`'s
 * `CONTACT_LOG_OUTCOME_TAGS` (FR-9.26) — the closed, non-participant-identifying contact-date
 * log outcome-tag vocabulary. Optional on the append form; never a free-text control. */
export const CONTACT_LOG_OUTCOME_TAGS = ['contacted', 'no-answer', 'appointment-set'] as const;
export type ContactLogOutcomeTag = (typeof CONTACT_LOG_OUTCOME_TAGS)[number];

/** Mirrors `platform/clinical-study/src/study-subject/domain/study-subject.ts`'s
 * `STUDY_SUBJECT_LIFECYCLE_STATES` (Domain Model, Study Subject Lifecycle) — `PROVISIONAL` is
 * the create/operational-actions phase; `ENROLLED` unlocks scheduling; `CANCELLED` is terminal. */
export const STUDY_SUBJECT_LIFECYCLE_STATES = ['PROVISIONAL', 'ENROLLED', 'CANCELLED'] as const;
export type StudySubjectLifecycleState = (typeof STUDY_SUBJECT_LIFECYCLE_STATES)[number];

/** Mirrors `platform/clinical-study/src/visit/domain/visit.ts`'s `VISIT_LIFECYCLE_STATES`
 * (Domain Model, Visit Lifecycle) — drives the per-visit action set `ScheduleSection` offers. */
export const VISIT_LIFECYCLE_STATES = [
  'PLANNED',
  'SCHEDULED',
  'COMPLETED',
  'MISSED',
  'CANCELLED',
] as const;
export type VisitLifecycleState = (typeof VISIT_LIFECYCLE_STATES)[number];

/** Mirrors `platform/clinical-study/src/schedule-access/domain/at-risk-visit.ts`'s
 * `VISIT_RISK_STATUSES` — the compliance safety net's own two-state classification (plan
 * Increment 9's Resolution 6: deliberately just two, no third numeric-buffer tier). Drives the
 * at-risk table's own risk badge and filter (`AtRiskVisitsTable.tsx`). */
export const VISIT_RISK_STATUSES = ['ELAPSING', 'ELAPSED'] as const;
export type VisitRiskStatus = (typeof VISIT_RISK_STATUSES)[number];
